import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { generateId } from '@appblocks/utils';

const PROJECTS_CONFIG_ID = 'appblocks.projects';

export const projectSlice = createSlice({
    name: 'project',
    initialState: {
        project: {},
        projectId: '',
        loadingCode: false,
        code: [],
        lastValidated: undefined,
        lastChanged: undefined,
        validationErrors: [],
        featureErrors: {},
        changes: [],
        projectLoaded: false,
        projects: localStorage.getItem(PROJECTS_CONFIG_ID)
            ? JSON.parse(localStorage.getItem(PROJECTS_CONFIG_ID)) : {},
        changeIndex: 0,
        changeStack: [],
    },
    reducers: {
        setProject: (state, action) => {
            // state.project = JSON.parse(JSON.stringify(action.payload));
            const project = action.payload;
            const allFeatures = {};
            flattenFeatures(project.features, allFeatures);
            const newState = {
                ...state,
                code: [],
                projectLoaded: true,
                projectId: action.payload.id,
                lastValidated: undefined,
                lastChanged: new Date().valueOf(),
                changes: [],
                loadingCode: false,
                project: {
                    id: project.id,
                    name: project.name,
                    device: project.device,
                    description: project.description,
                    shortDescription: project.shortDescription,
                    version: project.version,
                    runtime: project.runtime,
                    features: allFeatures,
                },
            };

            return newState;
        },
        getProjectCode: (state, action) => {

        },
        setProjectCode: (state, action) => {
            return {
                ...state,
                code: action.payload,
            };
        },
        setProjectCodeLoading: (state, action) => {
            return {
                ...state,
                loadingCode: action.payload,
            };
        },
        setProjectProperty: (state, action) => {
            return {
                ...state,
                project: {
                    ...state.project,
                    [action.payload.property]: action.payload.value,
                },
            };
        },
        setLastValidated: (state, action) => {
            state.lastValidated = action.payload;
        },
        projectSetBinaryId: (state, action) => {
            let currentProject = {};
            if (state.projects[action.payload.projectId] !== undefined) {
                currentProject = state.projects[action.payload.projectId];
            }
            const newState = {
                ...state,
                projects: {
                    ...state.projects,
                    [action.payload.projectId]: {
                        ...currentProject,
                        binaryId: action.payload.binaryId,
                    },
                },
            };
            localStorage.setItem(PROJECTS_CONFIG_ID, JSON.stringify(newState.projects));
            return newState;
        },
        projectSetDebugTarget: (state, action) => {
            let currentProject = {};
            if (state.projects[action.payload.projectId] !== undefined) {
                currentProject = state.projects[action.payload.projectId];
            }
            const newState = {
                ...state,
                projects: {
                    ...state.projects,
                    [action.payload.projectId]: {
                        ...currentProject,
                        device: action.payload.device,
                    },
                },
            };
            localStorage.setItem(PROJECTS_CONFIG_ID, JSON.stringify(newState.projects));
            return newState;
        },
        projectSetUploadMethod: (state, action) => {
            let currentProject = {};
            if (state.projects[action.payload.projectId] !== undefined) {
                currentProject = state.projects[action.payload.projectId];
            }
            const newState = {
                ...state,
                projects: {
                    ...state.projects,
                    [action.payload.projectId]: {
                        ...currentProject,
                        uploadMethod: action.payload.uploadMethod,
                    },
                },
            };
            localStorage.setItem(PROJECTS_CONFIG_ID, JSON.stringify(newState.projects));
            return newState;
        },
        updateFeatureSetting: (state, action) => {
            const {
                featureId, settingId, value,
            } = action.payload;
            const changesInState = {};
            const updatedSettings = featureId ? {} : action.payload.updatedSettings;
            if (featureId && state.project.features[featureId] !== undefined) {
                updatedSettings[featureId] = { ...action.payload.updatedSettings };
                if (settingId) {
                    updatedSettings[featureId][settingId] = value;
                }
            }
            const data = Object.keys(updatedSettings).map((featureId) => {
                return `${state.project.features[featureId].id}:[${Object.keys(updatedSettings[featureId]).map((settingKey) => {
                    return `${settingKey}=${updatedSettings[featureId][settingKey]}`;
                }).join(',')}]`;
            }).join(';');
            const featureIds = Object.keys(updatedSettings);
            for (let i = 0; i < featureIds.length; i++) {
                const featureId = featureIds[i];
                changesInState[featureId] = {
                    ...state.project.features[featureId],
                    settings: {
                        ...state.project.features[featureId].settings,
                        ...updatedSettings[featureId],
                    },
                };
            }
            const newChanges = pushChange(state.changes, {
                id: featureId,
                feature: featureIds.length === 1
                    ? state.project.features[featureIds[0]].id
                    : 'Multiple',
                type: 'update',
                data,
                time: Date.now(),
            });
            const newState = {
                ...state,
                code: [],
                changes: newChanges,
                project: {
                    ...state.project,
                    features: {
                        ...state.project.features,
                        ...changesInState,
                    },
                },
            };
            return newState;
        },
        addFeature: (state, action) => {
            const { feature, settingId, featureId } = action.payload;
            const newChanges = pushChange(state.changes, {
                id: featureId,
                feature: feature.id,
                type: 'add',
                data: '',
                time: Date.now(),
            });
            let newState = {
                ...state,
                code: [],
                changes: newChanges,
                project: {
                    ...state.project,
                    features: {
                        ...state.project.features,
                    },
                },
            };
            newState = addFeatureInner(newState, feature, featureId, settingId);
            return newState;
        },
        addFeatures: (state, action) => {
            const { features } = action.payload;
            const featureIds = [];
            for (let i = 0; i < features.length; i++) {
                featureIds.push(features[i].feature.id);
            }
            const newChanges = pushChange(state.changes, {
                id: undefined,
                feature: features.length === 1 ? features[0].feature.id : 'Multiple',
                type: 'add',
                data: features.length === 1 ? '' : featureIds.join(','),
                time: Date.now(),
            });
            let newState = {
                ...state,
                code: [],
                changes: newChanges,
                project: {
                    ...state.project,
                    features: {
                        ...state.project.features,
                    },
                },
            };
            for (let i = 0; i < features.length; i++) {
                newState = addFeatureInner(
                    newState,
                    features[i].feature,
                    features[i].featureId,
                    features[i].settingId,
                );
            }
            return newState;
        },
        removeFeature: (state, action) => {
            const { featureId } = action.payload;
            let newChanges = [...state.changes];
            if (state.project.features[featureId] !== undefined) {
                newChanges = pushChange(state.changes, {
                    id: featureId,
                    feature: state.project.features[featureId].id,
                    type: 'remove',
                    data: '',
                    time: Date.now(),
                });
            }
            const newState = removeFeatureInner(state, featureId);
            return {
                ...newState,
                changes: newChanges,
                code: [],
            };
        },
        removeFeatures: (state, action) => {
            const { features } = action.payload;
            const featureIds = [];
            for (let i = 0; i < features.length; i++) {
                if (state.project.features[features[i].featureId] === undefined) {
                    continue;
                }
                featureIds.push(state.project.features[features[i].featureId].id);
            }
            const newChanges = pushChange(state.changes, {
                id: undefined,
                feature: features.length === 1 ? state.project.features[features[0].featureId].id : 'Multiple',
                type: 'remove',
                data: features.length === 1 ? '' : featureIds.join(','),
                time: Date.now(),
            });
            let newState = {
                ...state,
                code: [],
                changes: newChanges,
                project: {
                    ...state.project,
                    features: {
                        ...state.project.features,
                    },
                },
            };
            for (let i = 0; i < features.length; i++) {
                newState = removeFeatureInner(
                    newState,
                    features[i].featureId,
                );
            }
            return newState;
        },
        swapFeatures: (state, action) => {
            const { featureId1, featureId2 } = action.payload;
            const features = Object.entries(state.project.features);
            let featureKey;
            let settingKey;
            for (let i = 0; i < features.length; i++) {
                const feature = features[i][1];
                const settings = Object.entries(feature.settings);
                for (let j = 0; j < settings.length; j++) {
                    if (Array.isArray(settings[j][1])) {
                        const arr = settings[j][1];
                        for (let k = 0; k < arr.length; k++) {
                            if (arr[k] === featureId1 || arr[k] === featureId2) {
                                settingKey = settings[j][0];
                                featureKey = features[i][0];
                                break;
                            }
                        }
                    }
                }
            }

            const newState = {
                ...state,
                project: {
                    ...state.project,
                    features: {
                        ...state.project.features,
                        [featureKey]: {
                            ...state.project.features[featureKey],
                            settings: {
                                ...state.project.features[featureKey].settings,
                                [settingKey]: [
                                    ...state.project.features[featureKey]
                                        .settings[settingKey].map((id) => {
                                            if (id === featureId1) {
                                                return featureId2;
                                            }
                                            if (id === featureId2) {
                                                return featureId1;
                                            }
                                            return id;
                                        }),
                                ],
                            },
                        },
                    },
                },
                code: [],
            };
            return newState;
        },
        setValidationErrors: (state, action) => {
            state.validationErrors = action.payload;
        },
        setFeatureErrors: (state, action) => {
            state.featureErrors = action.payload;
        },
        clearChanges: (state) => {
            return {
                ...state,
                changes: [],
            };
        },
        setLastChanged: (state, action) => {
            state.lastChanged = action.payload;
        },
        initChangeStack: (state, action) => {
            return {
                ...state,
                changeStack: [],
                changeIndex: 0,
            };
        },
        pushChangeStack: (state, action) => {
            let newStack = [...state.changeStack];
            if (state.changeIndex < newStack.length - 1) {
                newStack = newStack.slice(0, state.changeIndex + 1);
            }
            newStack.push(action.payload);
            if (newStack.length > 10) {
                newStack.shift();
            }
            return {
                ...state,
                changeIndex: newStack.length - 1,
                changeStack: newStack,
            };
        },
        setChangeIndex: (state, action) => {
            state.changeIndex = action.payload;
            state.projectLoaded = false;
        },
        createCloudDevice: (state, action) => {
        },
        setSimulatorStoredValues: (state, action) => {
            const { name, value } = action.payload;
            const newState = {
                ...state,
                projects: {
                    ...state.projects,
                    [state.projectId]: {
                        ...state.projects[state.projectId],
                        [name]: value,
                    },
                },
            };
            localStorage.setItem(PROJECTS_CONFIG_ID, JSON.stringify(newState.projects));
            return newState;
        },
        setSimulatorContext: (state, action) => {
            const newState = {
                ...state,
                projects: {
                    ...state.projects,
                    [state.projectId]: {
                        ...state.projects[state.projectId],
                        context: action.payload,
                    },
                },
            };
            return newState;
        },
    },
});

function flattenFeatures(features, allFeatures) {
    for (let i = 0; i < features.length; i++) {
        const feature = features[i];
        const tmp = {
            id: feature.id,
            settings: {},
        };
        // for (let j = 0; j < feature.settings.length; j++) {
        Object.keys(feature.settings).forEach((key) => {
            const settingValue = feature.settings[key];
            if (Array.isArray(settingValue)) {
                tmp.settings[key] = [];
                for (let k = 0; k < settingValue.length; k++) {
                    tmp.settings[key]
                        .push(settingValue[k].settings.ID);
                }
                flattenFeatures(settingValue, allFeatures);
            } else {
                tmp.settings[key] = settingValue;
            }
        });
        allFeatures[feature.settings.ID] = tmp;
    }
}

function addFeatureInner(state, feature, featureId, settingId) {
    const propertyKeys = Object.keys(feature.settings);
    let ID = feature.settings.ID;
    if (ID === undefined) {
        ID = generateId();
    }
    let newState = {
        ...state,
        project: {
            ...state.project,
            features: {
                ...state.project.features,
                [ID]: feature,
            },
        },
    };
    if (featureId !== undefined && settingId !== undefined) {
        const filteredSettings = state.project
            .features[featureId].settings[settingId].filter((setting) => {
                return typeof setting === 'string' || setting.settings.ID !== ID;
            });
        newState = {
            ...newState,
            project: {
                ...newState.project,
                features: {
                    ...newState.project.features,
                    [featureId]: {
                        ...newState.project.features[featureId],
                        settings: {
                            ...newState.project.features[featureId].settings,
                            [settingId]: [
                                ...filteredSettings,
                                ID,
                            ],
                        },
                    },
                    [ID]: feature,
                },
            },
        };
    }
    for (let i = 0; i < propertyKeys.length; i++) {
        if (Array.isArray(feature.settings[propertyKeys[i]])) {
            const children = feature.settings[propertyKeys[i]];
            for (let j = 0; j < children.length; j++) {
                newState = addFeatureInner(newState, children[j], ID, propertyKeys[i]);
            }
        }
    }
    return newState;
}

function removeFeatureInner(state, featureId) {
    const feature = state.project.features[featureId];
    if (feature === undefined) {
        return state;
    }
    let newState = {
        ...state,
        project: {
            ...state.project,
            features: _.omit(state.project.features, [featureId]),
        },
    };
    const propertyKeys = Object.keys(feature.settings);
    for (let i = 0; i < propertyKeys.length; i++) {
        if (Array.isArray(feature.settings[propertyKeys[i]])) {
            const children = feature.settings[propertyKeys[i]];
            for (let j = 0; j < children.length; j++) {
                newState = removeFeatureInner(newState, children[j]);
            }
        }
    }
    const featureKeys = Object.keys(newState.project.features);
    for (let i = 0; i < featureKeys.length; i++) {
        const tmpFeature = newState.project.features[featureKeys[i]];
        const settingsKeys = Object.keys(tmpFeature.settings);
        for (let j = 0; j < settingsKeys.length; j++) {
            const settingKey = settingsKeys[j];
            if (Array.isArray(tmpFeature.settings[settingKey])
                && tmpFeature.settings[settingKey].includes(featureId)) {
                newState = {
                    ...newState,
                    project: {
                        ...newState.project,
                        features: {
                            ...newState.project.features,
                            [featureKeys[i]]: {
                                ...newState.project.features[featureKeys[i]],
                                settings: {
                                    ...newState.project.features[featureKeys[i]].settings,
                                    [settingKey]: tmpFeature.settings[settingKey]
                                        .filter(id => id !== featureId),
                                },
                            },
                        },
                    },
                };
            }
        }
    }
    return newState;
}

function pushChange(changes, newChange) {
    const newChanges = [...changes];
    newChanges.push(newChange);
    if (newChanges.length > 20) {
        newChanges.shift();
    }
    return newChanges;
}

// Action creators are generated for each case reducer function
export const {
    setProject,
    setProjectCode,
    getProjectCode,
    setLastValidated,
    projectSetHash,
    projectSetDebugTarget,
    projectSetBinaryId,
    removeFeature,
    removeFeatures,
    addFeature,
    addFeatures,
    updateFeatureSetting,
    swapFeatures,
    setValidationErrors,
    setFeatureErrors,
    setProjectProperty,
    clearChanges,
    pushChangeStack,
    setChangeIndex,
    initChangeStack,
    createCloudDevice,
    projectSetUploadMethod,
    setSimulatorContext,
    setSimulatorStoredValues,
} = projectSlice.actions;


const updateFeatureSettingShape = {
    payload: PropTypes.oneOfType([
        PropTypes.shape({
            featureId: PropTypes.string.isRequired,
            settingId: PropTypes.string.isRequired,
            tutu: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
                PropTypes.bool,
            ]).isRequired,
            updatedSettings: PropTypes.shape({
                [PropTypes.string]: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number,
                    PropTypes.bool,
                ]),
            }),
        }),
        PropTypes.shape({
            updatedSettings: PropTypes.shape({
                [PropTypes.string]: PropTypes.shape({
                    [PropTypes.string]: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number,
                        PropTypes.bool,
                    ]),
                }),
            }),
        }),
    ]).isRequired,
};


export default projectSlice.reducer;
